import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { FieldRenderer } from "@/components/admin/cms/CmsFields";
import { can, MANAGE_CONTENT } from "@/lib/format";
import { toast } from "sonner";
import {
  HOME_BLOCK_TYPES, DEFAULT_HOME_BLOCKS, mergeHomeBlocks, cloneBlocks,
  HOME_PUBLISHED_KEY, HOME_DRAFT_KEY,
} from "@/lib/cms";
import {
  GripVertical, Pencil, ArrowUp, ArrowDown, RotateCcw, EyeOff, Eye, UploadCloud, Save, History,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HistoryDialog from "@/components/admin/cms/HistoryDialog";
import ScheduleControl from "@/components/admin/cms/ScheduleControl";

function blockSummary(block) {
  const c = block.content || {};
  switch (block.type) {
    case "hero": return c.line1 || "";
    case "trust": return `${(c.items || []).length} highlight${(c.items || []).length === 1 ? "" : "s"}`;
    case "military": return c.title || "";
    case "top_picks": return c.title || "";
    case "vip_tiers": return c.title || "";
    default: return "";
  }
}

function SortableBlockRow({ block, index, total, editable, onToggle, onEdit, onMove }) {
  const schema = HOME_BLOCK_TYPES[block.type] || {};
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`cms-block-${block.type}`}
      className={`flex items-center gap-3 rounded-md border p-3 bg-[var(--panel)] transition-colors ${
        block.enabled ? "border-[var(--border-lch)]" : "border-dashed border-[var(--border-lch)] opacity-70"
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        disabled={!editable}
        aria-label="Drag to reorder"
        data-testid={`cms-drag-${block.type}`}
        className="text-[var(--muted)] hover:text-[var(--text)] cursor-grab active:cursor-grabbing disabled:opacity-40 disabled:cursor-not-allowed touch-none"
      >
        <GripVertical size={18} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[var(--text)] text-sm">{schema.label || block.type}</span>
          {schema.auto && <Badge variant="outline" className="text-[10px] bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">Auto content</Badge>}
          {!block.enabled && (
            <Badge variant="outline" className="text-[10px] bg-[var(--panel-2)] text-[var(--muted)] border-[var(--border-lch)]">
              <EyeOff size={10} className="mr-1" />Hidden
            </Badge>
          )}
        </div>
        <div className="text-xs text-[var(--muted)] truncate mt-0.5">{blockSummary(block)}</div>
      </div>

      <div className="hidden sm:flex flex-col text-[var(--muted)]">
        <button disabled={!editable || index === 0} onClick={() => onMove(index, index - 1)} data-testid={`cms-up-${block.type}`} aria-label="Move up" className="hover:text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed"><ArrowUp size={14} /></button>
        <button disabled={!editable || index === total - 1} onClick={() => onMove(index, index + 1)} data-testid={`cms-down-${block.type}`} aria-label="Move down" className="hover:text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed"><ArrowDown size={14} /></button>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={block.enabled}
          onCheckedChange={(v) => onToggle(block.id, v)}
          disabled={!editable}
          data-testid={`cms-toggle-${block.type}`}
          aria-label={`Toggle ${schema.label} visibility`}
        />
        <Button size="sm" variant="outline" onClick={() => onEdit(block.id)} data-testid={`cms-edit-${block.type}`}>
          <Pencil size={13} className="mr-1" />Edit
        </Button>
      </div>
    </div>
  );
}

export default function HomeBuilder() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_CONTENT);
  const [blocks, setBlocks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Prefer an in-progress draft; otherwise fall back to the published layout.
      let source = null;
      try {
        const d = await api.get(`/admin/content/${HOME_DRAFT_KEY}`);
        if (d.data?.blocks) source = d.data.blocks;
      } catch { /* no draft */ }
      if (!source) {
        try {
          const p = await api.get(`/admin/content/${HOME_PUBLISHED_KEY}`);
          if (p.data?.blocks) source = p.data.blocks;
        } catch { /* no published */ }
      }
      setBlocks(mergeHomeBlocks(source));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setBlocks((bs) => {
      const oldI = bs.findIndex((b) => b.id === active.id);
      const newI = bs.findIndex((b) => b.id === over.id);
      if (oldI < 0 || newI < 0) return bs;
      return arrayMove(bs, oldI, newI);
    });
  };
  const move = (from, to) => setBlocks((bs) => (to < 0 || to >= bs.length ? bs : arrayMove(bs, from, to)));
  const toggle = (id, v) => setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, enabled: v } : b)));
  const setEditingContent = (key, val) =>
    setBlocks((bs) => bs.map((b) => (b.id === editingId ? { ...b, content: { ...b.content, [key]: val } } : b)));

  const editingBlock = blocks?.find((b) => b.id === editingId) || null;
  const editingSchema = editingBlock ? HOME_BLOCK_TYPES[editingBlock.type] : null;

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await api.put(`/admin/content/${HOME_DRAFT_KEY}`, { blocks });
      toast.success("Draft saved (not yet live).");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save draft");
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const preview = async () => {
    const ok = await saveDraft();
    if (ok) window.open("/?preview=1", "_blank", "noopener");
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Publish to live, and keep the draft in sync with what's now live.
      await api.put(`/admin/content/${HOME_PUBLISHED_KEY}`, { blocks });
      await api.put(`/admin/content/${HOME_DRAFT_KEY}`, { blocks });
      toast.success("Homepage published \u2014 changes are now live.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  const resetDefaults = () => {
    setBlocks(cloneBlocks(DEFAULT_HOME_BLOCKS));
    toast.info("Reset to the default layout \u2014 Save Draft or Publish to keep it.");
  };

  if (loading) {
    return (
      <div data-testid="cms-home-loading" className="space-y-3">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-16 rounded-md bg-[var(--panel-2)] animate-pulse" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="cms-home-error" className="text-center py-20">
        <p className="text-[var(--text-2)]">We couldn't load the homepage content.</p>
        <Button onClick={load} className="mt-4" variant="outline" data-testid="cms-home-retry">Try again</Button>
      </div>
    );
  }

  return (
    <div data-testid="cms-home">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-[var(--muted)]">Homepage sections</div>
        {editable && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)} data-testid="cms-home-history" className="text-[var(--text-2)] hover:text-[var(--text)]">
              <History size={13} className="mr-1" />History
            </Button>
            <Button size="sm" variant="ghost" onClick={resetDefaults} className="text-[var(--text-2)] hover:text-[var(--text)]" data-testid="cms-reset-defaults">
              <RotateCcw size={13} className="mr-1" />Reset to default
            </Button>
          </div>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={(blocks || []).map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2.5" data-testid="cms-block-list">
            {(blocks || []).map((b, i) => (
              <SortableBlockRow
                key={b.id}
                block={b}
                index={i}
                total={blocks.length}
                editable={editable}
                onToggle={toggle}
                onEdit={setEditingId}
                onMove={move}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editable && (
        <div className="mt-5">
          <ScheduleControl contentKey={HOME_PUBLISHED_KEY} getPayload={() => ({ blocks })} />
        </div>
      )}

      {editable && (
        <div className="flex flex-wrap items-center justify-end gap-2 sticky bottom-4 mt-6">
          <Button onClick={saveDraft} disabled={savingDraft} variant="outline" data-testid="cms-save-draft" className="bg-[var(--panel)]">
            <Save size={14} className="mr-1.5" />{savingDraft ? "Saving…" : "Save Draft"}
          </Button>
          <Button onClick={preview} variant="outline" data-testid="cms-preview" className="bg-[var(--panel)]">
            <Eye size={14} className="mr-1.5" />Preview
          </Button>
          <Button onClick={publish} disabled={publishing} data-testid="cms-publish" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)] shadow-lg">
            <UploadCloud size={14} className="mr-1.5" />{publishing ? "Publishing…" : "Publish"}
          </Button>
        </div>
      )}

      <Sheet open={!!editingId} onOpenChange={(o) => { if (!o) setEditingId(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-[var(--panel)] text-[var(--text)] border-[var(--border-lch)]" data-testid="cms-edit-sheet">
          {editingBlock && editingSchema && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[var(--text)]">Edit · {editingSchema.label}</SheetTitle>
                <SheetDescription className="text-[var(--text-2)]">{editingSchema.description}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 py-5">
                {editingSchema.fields.map((f) => (
                  <FieldRenderer
                    key={f.key}
                    field={f}
                    value={editingBlock.content[f.key]}
                    onChange={(v) => setEditingContent(f.key, v)}
                    disabled={!editable}
                  />
                ))}
              </div>
              <SheetFooter>
                <Button onClick={() => setEditingId(null)} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]" data-testid="cms-edit-done">Done</Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <HistoryDialog contentKey={HOME_PUBLISHED_KEY} open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />
    </div>
  );
}
